// A segmented control — one row of connected buttons, exactly one selected.
//
// It exists for the Codes tab's view switcher (owner, 2026-09-01: "Is there a
// way to make the References > Codes view switcher more prominent or
// distinctive? It looks like a filter, but it is really more than that"). It
// WAS a dropdown, sitting first in a row of dropdowns, so it read as a third
// filter — but Accounts / Departments / Codes are three different RECORDS, not
// three narrowings of one list. A segmented control says "pick a mode" the way
// a dropdown says "narrow this".
//
// Hand-rolled because Fluent v9 has no segmented control. It is deliberately
// NOT a `TabList`: this screen already nests one TabList inside another
// (References → Codes), and a third row of tabs would read as a third level of
// navigation rather than as a switch over one tab's content.
//
// Accessibility: a `radiogroup`, because that is what it is — one choice from a
// small fixed set, all options visible. Arrow keys move between the buttons the
// way they would between radios, and the selected one is the only tab stop.
import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { useRef } from 'react'

const useStyles = makeStyles({
  group: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    padding: '2px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  item: {
    borderRadius: tokens.borderRadiusSmall,
    fontWeight: tokens.fontWeightRegular,
    minWidth: '92px',
    // A transparent border of the same width as the selected segment's blue
    // one, so the segments do not shift by a pixel as the selection moves.
    border: '1px solid transparent',
  },
  // The selected segment is a raised surface against the group's recessed one —
  // the standard segmented-control treatment, and legible in both themes
  // because both colours are tokens rather than a tinted brand fill.
  // A BLUE OUTLINE ON THE RAISED SEGMENT (owner, 2026-09-14: "to make that
  // slider stand out a little bit better, can we put a blue border around the
  // white box that covers the currently selected option within the slider, to
  // parallel the blue line that sits underneath a chosen tab and sub-tab").
  // Same `colorBrandStroke1` the tab underline and the modal action bars use,
  // so "this is the selected one" is one colour throughout the app.
  //
  // The full `border` shorthand, not `borderColor` — griffel rejects the
  // four-sided sub-shorthand (`app/README.md`).
  selected: {
    backgroundColor: tokens.colorNeutralBackground1,
    fontWeight: tokens.fontWeightSemibold,
    boxShadow: tokens.shadow2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1 },
  },
})

export function SegmentedControl<T extends string>({
  ariaLabel,
  value,
  options,
  onChange,
}: {
  ariaLabel: string
  value: T
  options: readonly { value: T; label: string }[]
  onChange: (v: T) => void
}) {
  const s = useStyles()
  const group = useRef<HTMLDivElement>(null)

  // Arrow keys walk the group, wrapping at both ends, and move the selection
  // with the focus — the behaviour a radio group has natively and a row of
  // buttons does not.
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const delta = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
    if (delta === 0) return
    e.preventDefault()
    const at = options.findIndex((o) => o.value === value)
    const next = options[(at + delta + options.length) % options.length]
    if (!next) return
    onChange(next.value)
    // Focus follows selection, or the arrow key would move the highlight and
    // leave the keyboard behind on the old segment.
    group.current?.querySelector<HTMLButtonElement>(`[data-segment="${next.value}"]`)?.focus()
  }

  return (
    <div ref={group} className={s.group} role="radiogroup" aria-label={ariaLabel} onKeyDown={onKeyDown}>
      {options.map((o) => {
        const isSelected = o.value === value
        return (
          <Button
            key={o.value}
            data-segment={o.value}
            appearance="subtle"
            size="small"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isSelected ? 0 : -1}
            className={mergeClasses(s.item, isSelected && s.selected)}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </Button>
        )
      })}
    </div>
  )
}
